<template>
    <div v-if="documents.length === 0" class="DocumentDetailsContent">
        There are no documents attached.
    </div>
    <div v-else-if="currentDocument" class="DocumentDetailsContent">
        <BinaryDataDisplay
            v-if="isPreviewable"
            :key="`document-${currentIndex}-${currentDocument.mimeType}`"
            :value="currentDocumentValue"
            pdf-view-mode="zoom"
        />
        <div v-else class="DocumentDetailsContent__fallback">
            <span class="DocumentDetailsContent__fallbackTitle">
                Document preview unavailable
            </span>
            <span class="DocumentDetailsContent__fallbackSubtitle">
                Please download the document to view
            </span>
            <NewButton
                variant="fill"
                color="primary"
                @click="$emit('download', currentIndex)"
            >
                <EidIcon :icon-src="faDownload" alt="download" class="DocumentDetailsContent__fallbackDownloadIcon" />
                Download Document
            </NewButton>
        </div>
    </div>
</template>

<style lang="scss">
.DocumentDetailsContent {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    width: 100%;
    height: 100%;

    .BinaryDataDisplay {
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100%;
        flex: 1;
        min-height: 0;

        &__frame {
            height: 100%;
            flex: 1;
            min-height: 0;
        }
    }

    &__fallback {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        flex: 1;
        gap: 16px;
    }

    &__fallbackTitle {
        font-size: 14px;
        font-weight: 500;
        color: var(--eid-quality-black);
    }

    &__fallbackSubtitle {
        font-size: 12px;
        color: var(--eid-quality-black-75);
    }

    &__fallbackDownloadIcon {
        margin-right: 8px;
    }
}
</style>

<script lang="ts">
    import { Component, Prop, Vue } from '@evidentid/vue-property-decorator';

    import { faDownload } from '@fortawesome/pro-regular-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { isPreviewableMimeType } from '@evidentid/file-utils/mimeTypes';
    import { VerificationDocument } from '@evidentid/gateway-api-client/src/models/submission-tprm/VerificationDocument.model';

    import BinaryDataDisplay from '@/components/binary-data-display/BinaryDataDisplay.vue';
    import NewButton from '@/components/new-button/NewButton.vue';

    @Component({
        emits: [ 'download' ],
        components: {
            BinaryDataDisplay,
            EidIcon,
            NewButton,
        },
    })
    export default class DocumentDetailsContent extends Vue {
        private faDownload = faDownload;

        @Prop({ type: Array, required: true })
        private documents!: VerificationDocument[];

        @Prop({ type: Number, required: true })
        private currentIndex!: number;

        private get currentDocument(): VerificationDocument | null {
            return this.documents[this.currentIndex] || null;
        }

        private get isPreviewable(): boolean {
            return this.currentDocument ? isPreviewableMimeType(this.currentDocument.mimeType) : false;
        }

        private get currentDocumentValue(): { data: string, mimeType: string } | null {
            const doc = this.currentDocument;
            if (!doc) {
                return null;
            }
            // Return stable reference - only create new object if document actually changed
            return {
                data: doc.data,
                mimeType: doc.mimeType,
            };
        }
    }
</script>
